import { CellBase, Point, Spreadsheet } from 'react-spreadsheet';
import { useEffect, useState } from 'react';
import { SpreadsheetToolbar } from './SmartAttachmentSpreadsheetToolbar';
import { StyleType } from './SmartAttachmentSpreadsheet.types';

interface Props {
  data: any;
  onChange: (e: any) => void;
  onSpreadsheetChangeStyle: (className: string) => void;
  isFocused: boolean;
  hideIndicators?: boolean;
  baseClassName?: string;
}

/**
 * Spreadsheets component
 */
export const SmartAttachmentSpreadsheet = ({
  data,
  onChange,
  onSpreadsheetChangeStyle,
  isFocused,
  baseClassName,
}: Props) => {
  // array of cells that are currently selected
  const [selection, setSelection] = useState<Point[]>();
  // data with className attribute
  const [styledData, setStyledData] = useState<any>(data);
  // base className
  const [className, setClassName] = useState<string>(baseClassName ?? '');
  /**
   * Function to test if arrays are equal
   * @param arr1
   * @param arr2
   * @returns bool
   */
  const arraysAreEqual = (arr1: any[], arr2: any[]) => {
    if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (
        arr1[i].length !== arr2[i].length ||
        arr1[i].row !== arr2[i].row ||
        arr1[i].column !== arr2[i].column
      ) {
        return false;
      }
    }
    return true;
  };

  /**
   * Sets new selection
   * @param selection
   */
  const handleSetNewSelection = (selection: Point[]) => setSelection(selection);

  /**
   * Check if cell is currently selected
   * @param rowIndex
   * @param cellIndex
   * @param selection
   */
  const isCellSelected = (rowIndex: number, cellIndex: number, selection?: Point[]) => {
    if (!selection) return false;
    return selection.some(
      (selection) => selection.row === rowIndex && selection.column === cellIndex,
    );
  };

  /**
   * Toggles styles that cannot be used at once
   * @param style
   * @param className
   * @param styleType
   */
  const toggleStyle = (style: string, className?: string, styleType?: StyleType) => {
    // if no previous class return style
    if (!className || className.length === 0) return style;
    let _className = className ? className.trim() : '';
    // for classes that are not compatible
    // cant use 'text-red-500' and 'text-blue-500' at once
    if (styleType) {
      // find class to replace
      // bg-red-500 for bg-blue-500 etc.
      const pattern = styleType.after
        ? new RegExp(`(${styleType.before})(.*?)(${styleType.after})`)
        : new RegExp(`(${styleType.before})(.*?)`);
      const match = _className.match(pattern);
      if (match) {
        // replace items
        const final = _className.replace(match[0], style);
        return final;
        // class from style was not used --> add to current class
      } else return _className + ' ' + style;
    } else if (_className && _className.includes(style)) {
      // remove style if exists
      return _className.replace(style, ``);
    } else return _className.length > 1 ? (_className += ` ` + style) : style;
  };

  /**
   * Change style of selection
   */
  const changeColClass = (styles: string, styleType?: StyleType) => {
    const _styledData = data.map((row: any, rowIndex: number) => {
      return row.map((cell: CellBase, cellIndex: number) => {
        const isSelected = isCellSelected(rowIndex, cellIndex, selection);

        return {
          ...cell,
          value: cell.value,
          className: isSelected ? toggleStyle(styles, cell?.className, styleType) : cell.className,
        };
      });
    });
    setStyledData(_styledData);
  };

  /**
   * register changes in parent component
   */
  useEffect(() => {
    onChange(styledData);
  }, [styledData]);

  /**
   * Load new data to styled structure
   */
  useEffect(() => {
    if (!arraysAreEqual(data, styledData)) setStyledData(data);
  }, [data]);

  /**
   * Set base style for spreadsheet
   */
  useEffect(() => {
    if (!className) return;
    onSpreadsheetChangeStyle(className);
  }, [className]);

  return (
    <div className="relative">
      {isFocused && (
        <SpreadsheetToolbar
          data={styledData}
          baseClassName={className}
          setBaseClassName={setClassName}
          changeColClass={changeColClass}
          changeStructure={onChange}
          selection={selection}
        />
      )}
      <div className={`p-1 truncate`} style={{ maxWidth: 'inherit' }}>
        <Spreadsheet
          data={styledData}
          onChange={onChange}
          hideColumnIndicators={!isFocused}
          hideRowIndicators={!isFocused}
          onSelect={(newSelection) => {
            if (selection === undefined) return handleSetNewSelection(newSelection);
            if (!arraysAreEqual(selection, newSelection) && newSelection.length > 0)
              return handleSetNewSelection(newSelection);
          }}
          className={className}
        />
      </div>
    </div>
  );
};
